import { create, v, w } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/framework/core/middleware/theme';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import Button from '@dojo/widgets/button';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';

import { createNlsMiddleware, registry, userstate, landingChange, documentapi } from 'lcc-state';
import { QuizChoice } from '@holmescorp/lcc-app';
import { publish } from '@holmescorp/pub-sub';
import { resourceDitabookSelector } from '@holmescorp/resource-parser';
import { Icon } from '@holmescorp/base-widgets/icon';
import { convertToBoolean } from '@holmescorp/common';

import css from '../css/survey.m.css';
import SurveyQuestions from '../support/questions';

export interface SurveyProperties {}

interface SurveyIcache {
	showQuestions: boolean;
	welcomePage: number;
}

const nls = createNlsMiddleware('landing');
const icache = createICacheMiddleware<SurveyIcache>();
const factory = create({
	theme,
	nls,
	userstate,
	icache,
	documentapi
}).properties<SurveyProperties>();

export const Survey = factory(({ middleware: { theme, nls, userstate, icache, documentapi } }) => {
	const themeCss = theme.classes(css);
	const { get: uget, path: upath } = userstate;

	const {
		messages: {
			header = '[landing] header',
			survey_confirm_quiz = '[landing] survey_confirm_quiz',
			survey_confirm_button = '[landing] survey_confirm_button',
			survey_quiz_type = '[landing] survey_quiz_type',
			survey_landing_invite_button = '[landing] survey_landing_invite_button',
			survey_next_button_text = 'Next',
			survey_back_button_text = 'Back',
			survey_a11y_description = '[landing] survey_a11y_description',
			use_automatic_quiz_selection = '',
			automatic_quiz_selection = '[landing] automatic_quiz_selection'
		}
	} = nls();

	const quizSelected = uget(upath('quizPattern'));
	const showQuestions = icache.getOrSet('showQuestions', false);
	const welcomePage = icache.getOrSet('welcomePage', 1);

	const useAutomaticQuizSelection = convertToBoolean(use_automatic_quiz_selection);

	const confirm_quiz_choice = () => {
		landingChange({ complete: true });
		publish('site/assessment', survey_quiz_type);
	};

	const progress_dots = () => {
		const dots = [];

		for (let i = 1; i <= 3; i++) {
			dots.push(
				v('span', { classes: ['fa-stack', 'fa-sm'] }, [
					v('i', { classes: ['fa-regular', 'fa-circle', 'fa-stack-2x', themeCss.dot] }),
					i <= welcomePage
						? v('i', {
								classes: ['fa-solid', 'fa-circle', 'fa-stack-1x', 'fa-inverse', themeCss.activeDot]
						  })
						: null
					// v('i', { classes: ['fa-solid', 'fa-circle', 'fa-stack-1x', 'fa-inverse', i <= activeIndex ? css.activeDot : null] })
				])
			);
		}
		return v('div', { classes: [themeCss.root, themeCss.progress] }, dots);
	};

	const buttons = () => {
		if (welcomePage === 1) {
			return v('div', {}, [
				w(
					Button,
					{
						onClick: () => {
							icache.set('welcomePage', welcomePage + 1);
						},
						iconPosition: 'after'
					},
					[
						{
							label: survey_next_button_text,
							icon: w(Icon, { faname: 'arrow-right' })
						}
					]
				)
			]);
		} else {
			return v('div', { classes: [themeCss.buttons] }, [
				w(
					Button,
					{
						onClick: () => {
							icache.set('welcomePage', welcomePage - 1);
						}
					},
					[
						{
							label: survey_back_button_text,
							icon: w(Icon, { faname: 'arrow-left' })
						}
					]
				),
				w(
					Button,
					{
						onClick: () => {
							if (welcomePage === 3) {
								icache.set('showQuestions', true);
							} else {
								icache.set('welcomePage', welcomePage + 1);
							}
						},
						iconPosition: 'after'
					},
					[
						{
							label: welcomePage === 3 ? survey_landing_invite_button : survey_next_button_text,
							icon: w(Icon, { faname: 'arrow-right' })
						}
					]
				)
			]);
		}
	};

	documentapi.setTitle(survey_a11y_description);

	if (useAutomaticQuizSelection && showQuestions) {
		landingChange({ complete: true });
		publish('site/assessment', automatic_quiz_selection);
		return;
	}

	return [
		quizSelected
			? v('div', { classes: [themeCss.root, themeCss.pathSelect] }, [
					v('p', { classes: [themeCss.confirm] }, [survey_confirm_quiz]),
					w(QuizChoice, {}),
					v('div', { classes: [themeCss.startButton] }, [
						w(Button, { onClick: confirm_quiz_choice }, [survey_confirm_button])
					])
			  ])
			: showQuestions
			? v('div', { classes: [themeCss.root] }, [
					v('h3', { classes: [themeCss.header] }, [header]),
					w(SurveyQuestions, {})
			  ])
			: v('div', { classes: [themeCss.root, themeCss.welcome], id: 'tabPanelAssess' }, [
					w(resourceDitabookSelector, {
						topic: `welcome_page_${welcomePage}`,
						key: `welcome_page_${welcomePage}`
					}),
					progress_dots(),
					buttons()
			  ])
	];
});

registry.define('LandingPageSurvey', Survey);
export default Survey;
